import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Bot, Send, Loader2, MessageCircle, X, Sparkles } from "lucide-react";
import { Button } from "./ui/button";
import api from "../lib/api";

/**
 * Widget chat AI per il Codice Penale.
 * - Stateless lato server (no DB persistence)
 * - History gestita SOLO in memoria (max 10 messaggi nella sessione)
 * - Floating bottom-right su mobile e desktop
 */
export default function PenalCodeChatbot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Ciao! Sono l'Assistente AI del Codice Penale di San Andreas. Chiedimi quale capo d'imputazione applicare, o quale pena prevede un articolo." },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async () => {
    const txt = input.trim();
    if (!txt || sending) return;
    const next = [...messages, { role: "user", content: txt }];
    setMessages(next);
    setInput("");
    setSending(true);
    try {
      const { data } = await api.post("/penal-code/chat", {
        message: txt,
        history: next.slice(-6, -1), // ultimi 5 turni di contesto
      });
      setMessages([...next, { role: "assistant", content: data.reply || "—" }]);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Errore: l'assistente non è raggiungibile";
      setMessages([...next, { role: "assistant", content: `⚠️ ${detail}` }]);
    } finally {
      setSending(false);
    }
  };

  // Rendiamo il widget via Portal in document.body per evitare che sia
  // "catturato" da qualunque ancestor con transform/filter/will-change che
  // creerebbe un containing block sostitutivo per position:fixed.
  const content = (
    <>
      {/* Floating button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-[9999] bg-sheriff-gold hover:bg-sheriff-gold-dark text-[#0A1F18] font-bold rounded-full shadow-2xl shadow-sheriff-gold/40 px-5 py-3 flex items-center gap-2 transition-transform hover:scale-105"
          data-testid="penal-chat-open"
        >
          <Sparkles className="h-4 w-4" />
          <span className="uppercase-label text-sm">Chiedi all&apos;AI</span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          className="fixed bottom-6 right-6 z-[9999] w-[min(420px,calc(100vw-2rem))] h-[min(560px,calc(100vh-3rem))] bg-[#0A1F18] border border-sheriff-gold/40 rounded-2xl shadow-2xl shadow-sheriff-gold/30 flex flex-col overflow-hidden"
          data-testid="penal-chat-panel"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-sheriff-gold/15 to-transparent border-b border-sheriff-gold/30 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bot className="h-5 w-5 text-sheriff-gold" />
              <div>
                <div className="text-[#F5E6B8] font-heading font-bold text-sm">Assistente Codice Penale</div>
                <div className="text-[10px] text-[#B8A269] uppercase-label">San Andreas · powered by AI</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-[#8B7B49] hover:text-rose-300" data-testid="penal-chat-close">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2.5 scrollbar-thin">
            {messages.map((m, i) => (
              <ChatBubble key={`${i}-${m.role}`} role={m.role} content={m.content} />
            ))}
            {sending && (
              <div className="flex items-center gap-2 text-xs text-[#B8A269] pl-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Sto consultando il codice…
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-sheriff-gold/20 p-2.5 flex gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Es. Quale capo per furto aggravato con scasso?"
              className="flex-1 bg-[#143830] border border-sheriff-gold/30 rounded-full px-4 py-2 text-sm text-[#F5E6B8] placeholder:text-[#8B7B49] focus:outline-none focus:border-sheriff-gold"
              disabled={sending}
              maxLength={1000}
              data-testid="penal-chat-input"
            />
            <Button onClick={send} disabled={sending || !input.trim()} className="bg-sheriff-gold hover:bg-sheriff-gold-dark text-[#0A1F18] rounded-full px-3 disabled:opacity-50" data-testid="penal-chat-send">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      )}
    </>
  );

  if (typeof document === "undefined") return null;
  return createPortal(content, document.body);
}

function ChatBubble({ role, content }) {
  const isUser = role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap ${isUser ? "bg-sheriff-gold/20 text-[#F5E6B8] border border-sheriff-gold/30" : "bg-[#143830] text-[#E8D89A] border border-sheriff-gold/15"}`}>
        {!isUser && (
          <div className="text-[10px] uppercase-label text-sheriff-gold mb-0.5 flex items-center gap-1">
            <Sparkles className="h-2.5 w-2.5" /> AI
          </div>
        )}
        {renderWithCodes(content)}
      </div>
    </div>
  );
}

/** Evidenzia i [code] nel testo della risposta */
function renderWithCodes(text) {
  const parts = text.split(/(\[[A-Za-z0-9.()\s/-]+\])/g);
  return parts.map((p, i) => {
    const key = `${i}-${p.slice(0, 24)}`;
    if (/^\[.+\]$/.test(p)) {
      return <span key={key} className="inline-block bg-sheriff-gold/25 text-sheriff-gold rounded px-1.5 py-0.5 mx-0.5 font-mono text-xs font-bold">{p.slice(1, -1)}</span>;
    }
    return <span key={key}>{p}</span>;
  });
}
